import { useContext } from "react";

import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { DateTimeWithPrecision } from "@/Common/Models/DateTimeWithPrecision";
import { TextInput } from "@/DesignSystem";
import { EncounterDateContext } from "@/Flowsheets/Contexts/EncounterDateContext";
import { AgentDoseReviewedCareElement } from "@/Flowsheets/Models/CareElements/AgentDoseReviewedCareElement";
import { CareElementDateReviewedRow } from "./CareElementDateReviewedRow";

import styles from "./CareElementAgentDoseDateReviewed.module.scss";

interface IAgentDoseReviewedCareElementProps {
    isEditable: boolean;
    label: string;
    side?: "row" | "column";
    element: AgentDoseReviewedCareElement;
    onChange: (element: AgentDoseReviewedCareElement) => void;
}

export function CareElementAgentDoseDateReviewed(props: Readonly<IAgentDoseReviewedCareElementProps>): JSX.Element {
    const { element, onChange, isEditable, side = "row", label } = props;
    const { encounterDateTimeWithPrecision } = useContext(EncounterDateContext);
    const { getText } = useTranslation();

    // Compute the date reviewed based on the next Agent/Dose state.
    // - If there is content (non-empty agent or dose):
    //   - If no actual date value is set yet, use the encounter date.
    //   - Otherwise, keep the existing date.
    // - If both fields are empty, clear the date.
    function computeDateReviewed(nextAgent: string, nextDose: string): DateTimeWithPrecision {
        const hasContent = nextAgent?.trim().length || nextDose?.trim().length;

        if (!hasContent) {
            return null;
        }

        const hasExistingDateValue = Boolean(element.DateReviewedWithPrecision?.value);
        return hasExistingDateValue ? element.DateReviewedWithPrecision : encounterDateTimeWithPrecision;
    }

    return (
        <CareElementDateReviewedRow
            element={element}
            onChange={onChange}
            isEditable={isEditable}
            label={label}
            side={side}
            isDatePickerVisible={!!(element.Agent?.trim().length || element.Dose?.trim().length)}
        >
            <div className={styles.inputContainer}>
                <TextInput
                    id={`care-element-agent-${element.Name}`}
                    label={getText("Flowsheets_HeartFailureBasicCareComponentLabel_Agent")}
                    value={element.Agent ?? ""}
                    disabled={!isEditable}
                    labelPosition="top"
                    onChange={event => {
                        const text = event.target.value;
                        const nextDate = computeDateReviewed(text, element.Dose ?? "");
                        onChange(new AgentDoseReviewedCareElement({ ...element, Agent: text, DateReviewedWithPrecision: nextDate }));
                    }}
                    maxLength={200}
                />
                <TextInput
                    id={`care-element-dose-${element.Name}`}
                    label={getText("Flowsheets_HeartFailureBasicCareComponentLabel_Dose")}
                    value={element.Dose ?? ""}
                    disabled={!isEditable}
                    labelPosition="top"
                    onChange={event => {
                        const text = event.target.value;
                        const nextDate = computeDateReviewed(element.Agent ?? "", text);
                        onChange(new AgentDoseReviewedCareElement({ ...element, Dose: text, DateReviewedWithPrecision: nextDate }));
                    }}
                    maxLength={200}
                />
            </div>
        </CareElementDateReviewedRow>
    );
}
